// PHA-626: Personalized Onboarding Script.
//
// A short, plain-English script the care team reads to introduce the program and the
// patient's care plan BEFORE the patient agrees to enroll. It is assembled deterministically
// (no LLM) from section texts with placeholders, filled per patient. The clinician brief that
// sits above the script in the dialog is a separate, AI-written block (clinicianBrief.ts).
//
// Round 3 (Zack, 2026-11-02 comment + 2026-11-03 call):
//   - Phanova's default wording = the Phanova clinic's own wording on dev (organization 3).
//   - Every section has an "Include this section" switch (on by default); its text is always
//     editable and saved with the clinic settings.
//   - "What We Ask Of You" and read-aloud are gone.
//   - Before there is any care plan, {conditions} reads "your main chronic conditions" and the
//     problem-list names appear next to it as a NOTE for the caller (not read to the patient).
//
// English only: the plain-English care plan blob is stored in English.
import type { PatientFriendlyPlan } from '../types/patient';
import { CARE_PLAN_PROGRAM_NAMES } from './generateCarePlanPdf';
import type { CarePlanProgram } from './generateCarePlanPdf';

export type OnboardingScriptSectionId = 'overview' | 'value' | 'financial' | 'consent';

export interface OnboardingScriptSection {
  id: OnboardingScriptSectionId;
  heading: string;
  /** May contain a caller note; render with splitScriptNotes. */
  paragraphs: string[];
  bullets: string[];
  /** Read after the bullets (the consent section's closing question). */
  closing?: string;
}

export interface OnboardingScript {
  title: string;
  sections: OnboardingScriptSection[];
}

export interface OnboardingScriptInput {
  programs: CarePlanProgram[];
  /** APCM Tier 1 = general care plan (no qualifying chronic condition). */
  generalOnly?: boolean;
  firstName?: string | null;
  /** Real, patient-readable condition names (deriveManagedConditionNames). */
  conditionNames?: string[];
  patientFriendlyPlan?: PatientFriendlyPlan | null;
  /** False when the patient has no care plan (approved or draft) yet. Defaults to true. */
  hasCarePlan?: boolean;
  /** Problem-list names: shown to the caller as a note when there is no care plan. */
  problemNames?: string[];
  medicationCount?: number;
  clinicName?: string | null;
  /** The clinic's own wording / inclusion per section; the rest keep Phanova's default. */
  template?: OnboardingScriptTemplate | null;
}

export interface OnboardingScriptTemplateEntry {
  heading?: string;
  body?: string;
  /** Only stored when false: the section is left out of the script. */
  include?: boolean;
}

export type OnboardingScriptTemplate = Partial<Record<OnboardingScriptSectionId, OnboardingScriptTemplateEntry>>;

export const ONBOARDING_SCRIPT_SECTION_IDS: OnboardingScriptSectionId[] = ['overview', 'value', 'financial', 'consent'];

// Wording from the reference script's "Before we begin" consent section. It closes the script,
// so the script ends by asking for the patient's agreement.
export const CONSENT_QUESTION = 'Do you agree to take part in the program?';

/**
 * Phanova's default wording: the Phanova clinic's own text on dev (organizations.id = 3,
 * onboarding_script_template, read 2026-11-03), per Zack's item 7. Kept in code, not read
 * from that clinic at runtime, because organization ids differ between environments.
 */
export const PHANOVA_DEFAULT_SECTIONS: Record<OnboardingScriptSectionId, { heading: string; body: string }> = {
  overview: {
    heading: 'About the Program',
    body: [
      'Because you have significant chronic medical conditions, Medicare offers a care management program that allows our team to help monitor your health between visits. We check in with you, help manage medications, and coordinate your care to help prevent complications and keep you healthier.',
      'I think it would greatly benefit you and I recommend it.',
    ].join('\n'),
  },
  value: {
    heading: 'What This Means For You',
    body: [
      'We will focus on {conditions}. Your care team will help you keep it under control.',
      'Here is what your care plan aims to do for you. {goals}',
    ].join('\n\n'),
  },
  financial: {
    heading: 'What It May Cost',
    body: [
      'The program is covered by Medicare, which means you might have to pay a small copay or coinsurance, just like you would for a medical appointment.',
      'However, if you have a Medicare Supplement plan, Medicaid, or another type of secondary coverage, you could pay $0 out of pocket. In fact, many of our patients pay nothing for the program.',
    ].join('\n\n'),
  },
  consent: {
    heading: 'Before We Begin',
    body: [
      'You can stop any time: just let us know, and it will end at the close of that month.',
      'Before we start these services, we need your agreement.',
    ].join('\n\n'),
  },
};

/** Placeholders a clinic can use in its section text. */
export const ONBOARDING_SCRIPT_PLACEHOLDERS: { key: string; description: string }[] = [
  { key: 'first_name', description: "the patient's first name" },
  { key: 'clinic_name', description: "the clinic's name" },
  { key: 'programs', description: 'the programs offered, e.g. "Chronic Care Management (CCM)"' },
  {
    key: 'conditions',
    description:
      'the care plan\'s conditions; before there is a care plan, "your main chronic conditions" with the problem list as a note for the caller',
  },
  { key: 'medications', description: 'e.g. "your 4 medications"' },
  {
    key: 'goals',
    description:
      'the plain-English care plan goals; before there is a care plan, the whole line becomes "Once you join, we will build a care plan with you…"',
  },
  { key: 'tasks', description: 'on its own "- {tasks}" line, one bullet per "What To Do" step' },
];

// A caller note travels inside a paragraph between these private-use characters, so it
// survives placeholder filling and the renderer can style it apart from the spoken text.
const NOTE_START = '';
const NOTE_END = '';
const NOTE_RE = /([^]*)/g;

export interface ScriptSegment {
  text: string;
  /** True for a note to the caller, which is not read to the patient. */
  note: boolean;
}

/** Splits a script paragraph into spoken text and caller notes. */
export function splitScriptNotes(paragraph: string): ScriptSegment[] {
  const out: ScriptSegment[] = [];
  let last = 0;
  for (const m of Array.from(paragraph.matchAll(NOTE_RE))) {
    const at = m.index ?? 0;
    if (at > last) out.push({ text: paragraph.slice(last, at), note: false });
    out.push({ text: m[1], note: true });
    last = at + m[0].length;
  }
  if (last < paragraph.length) out.push({ text: paragraph.slice(last), note: false });
  return out.filter((s) => s.text.length > 0);
}

/** A paragraph as plain text, with any caller note in [square brackets]. */
const paragraphToText = (p: string): string => p.replace(NOTE_RE, (_w, n: string) => `[${n}]`);

const joinWithAnd = (items: string[]): string =>
  items.length <= 1 ? items.join('') : `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`;

const clean = (s?: string | null): string => (s ?? '').trim();

/** First definition with a non-empty plain-English blob (same pick as the printed PDF). */
export function pickPatientFriendlyPlan(
  defs?: { patient_friendly_plan?: PatientFriendlyPlan | null }[] | null,
): PatientFriendlyPlan | null {
  for (const d of defs ?? []) {
    const pfp = d.patient_friendly_plan;
    if (!pfp) continue;
    if (
      clean(pfp.goals) ||
      (pfp.tasks && pfp.tasks.length > 0) ||
      (pfp.medications && pfp.medications.length > 0) ||
      clean(pfp.barriers) ||
      (pfp.warning_signs && pfp.warning_signs.length > 0) ||
      (pfp.resources && pfp.resources.length > 0)
    ) {
      return pfp;
    }
  }
  return null;
}

// The caller's note lists the problem list, capped so it stays readable at a glance.
const MAX_PROBLEM_NAMES = 8;

function uniqueNames(raw: string[] | undefined): string[] {
  const seen = new Set<string>();
  const names: string[] = [];
  for (const r of raw ?? []) {
    const n = clean(r);
    if (!n || seen.has(n.toLowerCase())) continue;
    seen.add(n.toLowerCase());
    names.push(n);
  }
  return names;
}

function conditionsValue(input: OnboardingScriptInput): string {
  if (input.generalOnly) return 'your health';
  if (input.hasCarePlan === false) {
    const names = uniqueNames(input.problemNames);
    if (names.length === 0) return 'your main chronic conditions';
    const shown = names.length > MAX_PROBLEM_NAMES
      ? `${names.slice(0, MAX_PROBLEM_NAMES).join(', ')} and ${names.length - MAX_PROBLEM_NAMES} more`
      : names.join(', ');
    return `your main chronic conditions ${NOTE_START}(out of ${shown})${NOTE_END}`;
  }
  const fromPlan = uniqueNames(input.conditionNames);
  return fromPlan.length > 0 ? joinWithAnd(fromPlan) : 'your health';
}

const medicationsPhrase = (count?: number): string =>
  count === 1 ? 'your medication' : count && count > 1 ? `your ${count} medications` : 'your medications';

const GENERIC_GOALS =
  'Your care plan will set clear, simple health goals with you, and your team will check in on them every month.';
export const NO_CARE_PLAN_YET =
  'Once you join, we will build a care plan with you, with clear and simple health goals, and check in on them every month.';

function placeholderValues(input: OnboardingScriptInput): Record<string, string> {
  return {
    first_name: clean(input.firstName),
    clinic_name: clean(input.clinicName) || 'our clinic',
    programs: joinWithAnd(input.programs.map((p) => CARE_PLAN_PROGRAM_NAMES[p])) || 'our care program',
    conditions: conditionsValue(input),
    medications: medicationsPhrase(input.medicationCount),
    goals: clean(input.patientFriendlyPlan?.goals) || GENERIC_GOALS,
  };
}

const taskBullets = (input: OnboardingScriptInput): string[] =>
  (input.patientFriendlyPlan?.tasks ?? [])
    .map((t) => {
      const instruction = clean(t.instruction);
      const often = clean(t.how_often);
      if (!instruction) return '';
      return often ? `${instruction} (${often})` : instruction;
    })
    .filter(Boolean);

const PLACEHOLDER_RE = /\{([a-z_]+)\}/g;

export function findUnknownPlaceholders(text: string): string[] {
  const known = new Set(ONBOARDING_SCRIPT_PLACEHOLDERS.map((p) => p.key));
  const unknown = new Set<string>();
  for (const m of Array.from(text.matchAll(PLACEHOLDER_RE))) {
    if (!known.has(m[1])) unknown.add(m[1]);
  }
  return Array.from(unknown);
}

/**
 * Keeps the known sections' non-empty text and an explicit include=false; anything else
 * reads as "Phanova's default". Older saved entries (no include flag) count as included, and
 * the retired "expectations" section is dropped.
 */
export function normalizeOnboardingScriptTemplate(raw: unknown): OnboardingScriptTemplate {
  const out: OnboardingScriptTemplate = {};
  if (!raw || typeof raw !== 'object') return out;
  for (const id of ONBOARDING_SCRIPT_SECTION_IDS) {
    const entry = (raw as Record<string, { heading?: unknown; body?: unknown; include?: unknown } | undefined>)[id];
    if (!entry || typeof entry !== 'object') continue;
    const heading = typeof entry.heading === 'string' ? entry.heading.trim() : '';
    const body = typeof entry.body === 'string' ? entry.body.trim() : '';
    const excluded = entry.include === false;
    if (heading || body || excluded) {
      out[id] = { ...(heading ? { heading } : {}), ...(body ? { body } : {}), ...(excluded ? { include: false } : {}) };
    }
  }
  return out;
}

const fill = (line: string, values: Record<string, string>, tasks: string[]): string =>
  line
    .replace(PLACEHOLDER_RE, (whole, key: string) =>
      key === 'tasks' ? tasks.join('; ') : key in values ? values[key] : whole,
    )
    // An empty {first_name} must not leave "Hi , " behind.
    .replace(/\s+([,.!?])/g, '$1')
    .replace(/\s{2,}/g, ' ')
    .trim();

/** Section text: plain lines are paragraphs, "- " lines are bullets. */
function buildSection(
  id: OnboardingScriptSectionId,
  heading: string,
  body: string,
  input: OnboardingScriptInput,
): OnboardingScriptSection {
  const values = placeholderValues(input);
  const tasks = taskBullets(input);
  const noCarePlan = input.hasCarePlan === false;
  const paragraphs: string[] = [];
  const bullets: string[] = [];
  let saidNoCarePlan = false;
  for (const rawLine of body.split(/\r?\n/)) {
    const line = rawLine.trim();
    if (!line) continue;
    const isBullet = /^[-*•]\s+/.test(line);
    const text = line.replace(/^[-*•]\s+/, '');
    if (isBullet && text === '{tasks}') {
      bullets.push(...tasks);
      continue;
    }
    // "Here is what your care plan aims to do for you. {goals}" cannot be said about a plan
    // that does not exist, so before there is one the whole line becomes the "we will build
    // one with you" sentence (once, however many lines mention {goals}).
    if (noCarePlan && /\{goals\}/.test(text)) {
      if (!saidNoCarePlan) (isBullet ? bullets : paragraphs).push(NO_CARE_PLAN_YET);
      saidNoCarePlan = true;
      continue;
    }
    const filled = fill(text, values, tasks);
    if (filled) (isBullet ? bullets : paragraphs).push(filled);
  }
  return {
    id,
    heading: fill(heading, values, tasks) || PHANOVA_DEFAULT_SECTIONS[id].heading,
    paragraphs,
    bullets,
    ...(id === 'consent' ? { closing: CONSENT_QUESTION } : {}),
  };
}

export function buildOnboardingScript(input: OnboardingScriptInput): OnboardingScript {
  const first = clean(input.firstName);
  const template = input.template ?? {};
  const sections: OnboardingScriptSection[] = [];
  for (const id of ONBOARDING_SCRIPT_SECTION_IDS) {
    const entry = template[id];
    if (entry?.include === false) continue;
    const def = PHANOVA_DEFAULT_SECTIONS[id];
    sections.push(buildSection(id, entry?.heading || def.heading, entry?.body || def.body, input));
  }
  return {
    title: first ? `Onboarding Script for ${first}` : 'Onboarding Script',
    sections,
  };
}

/** Plain text for copy-to-clipboard; caller notes are kept in [square brackets]. */
export function onboardingScriptToText(script: OnboardingScript): string {
  return script.sections
    .map((s) =>
      [
        s.heading,
        ...s.paragraphs.map(paragraphToText),
        ...s.bullets.map((b) => `- ${paragraphToText(b)}`),
        ...(s.closing ? [s.closing] : []),
      ].join('\n'),
    )
    .join('\n\n');
}
